// ---------------------------------------------------------------------------
// 決算書作成ページ 型定義 (2026-05-29)
//   データ源: freee /api/1/reports/trial_bs|trial_pl (worker /api/freee-proxy 経由・GET読取)
//   重要(会社独自仕様): 本表の科目表示 = 決算書表示名(小カテゴリー)=account_category_name /
//                       内訳表示 = 勘定科目 = account_item_name
//   freee は hierarchy_level + total フラグで小計/階層を返す(自前集計不要)
// ---------------------------------------------------------------------------

/** 5帳票の種別 */
export type ReportKind =
  | 'bs'            // 合計残高試算表(BS)
  | 'pl'            // 合計残高試算表(PL)
  | 'pl_prev'       // 合計残高試算表(前期PL)
  | 'yoy'           // 科目別前年同月対比表(会社合計)
  | 'sub_breakdown'; // 補助科目内訳表(長期借入金・部門本部)

export const REPORT_KINDS: { key: ReportKind; label: string; landscape: boolean }[] = [
  { key: 'bs', label: '合計残高試算表（BS）', landscape: false },
  { key: 'pl', label: '合計残高試算表（PL）', landscape: false },
  { key: 'pl_prev', label: '合計残高試算表（前期PL）', landscape: false },
  { key: 'yoy', label: '科目別前年同月対比表（会社合計）', landscape: true },
  { key: 'sub_breakdown', label: '補助科目内訳表', landscape: false },
];

/** freee trial_bs / trial_pl の balances[] 1要素 (date-range 取得時の実フィールド) */
export interface FreeeTrialBalanceRow {
  account_item_id?: number | null;
  account_item_name?: string | null;        // 勘定科目 (leaf行のみ。total/group行は null)
  account_category_name?: string | null;     // 決算書表示名(小カテゴリー)
  account_group_name?: string | null;        // group モード時の決算書表示名
  parent_account_category_name?: string | null; // 上位カテゴリ (total行の名称)
  hierarchy_level?: number | null;
  opening_balance?: number | null;           // 前月残高(date-range単月時=前月末累計)
  debit_amount?: number | null;              // 当月借方
  credit_amount?: number | null;             // 当月貸方
  closing_balance?: number | null;           // 残高(当月末累計)
  composition_ratio?: number | null;         // freee構成比(本表では自前再計算する)
  total_line?: boolean | null;               // 小計/合計行か
}

export interface FreeeTrialResponse {
  trial_bs?: { balances: FreeeTrialBalanceRow[] };
  trial_pl?: { balances: FreeeTrialBalanceRow[] };
  up_to_date?: boolean;
  up_to_date_reasons?: { code: string; message: string }[];
}

/** 整形後: 帳票の1行 */
export interface ReportRow {
  /** 表示名: 本表=決算書表示名 / 内訳=勘定科目 */
  label: string;
  /** 列の数値(列定義 columns と同順) */
  values: (number | null)[];
  hierarchyLevel: number;
  isSubtotal: boolean;   // total_line=true の小計
  isTotal: boolean;      // 最終合計(資産合計/負債純資産合計/当期純利益 等)
  indent: number;
  accountItemId?: number | null;
  /** layout 駆動レンダリング用の行種別(blank=群間の空行 / detail / subtotal / grandtotal / crossref) */
  kind?: 'detail' | 'subtotal' | 'grandtotal' | 'crossref' | 'blank';
  /** 群間空行(レイアウト保持)。kind==='blank' のとき true */
  isBlank?: boolean;
  /** テンプレに無い freee 新規勘定科目を自動挿入した行か(meta.unmapped と併せて警告色表示) */
  isAutoInserted?: boolean;
  /** Excel テンプレ由来だが freee に値が無く0表示にした行か(科目廃止) */
  isZeroFilledRetired?: boolean;
}

/** ロバスト化: テンプレ未登録の freee 勘定科目を所属群へ自動挿入できなかった記録 */
export interface UnmappedAccount {
  /** freee 勘定科目名 */
  accountName: string;
  /** freee 決算書表示名(account_category_name) */
  categoryName: string;
  /** 当月末残高(または PL 当月発生) */
  amount: number;
}

/** ロバスト化: テンプレ未登録だが群を判定して自動挿入できた科目の記録 */
export interface AutoInsertedAccount {
  accountName: string;
  categoryName: string;
  /** 挿入先の群(小計)ラベル */
  group: string;
  amount: number;
}

export interface ReportColumn {
  key: string;
  label: string;
  /** 'currency' | 'ratio' | 'text' */
  fmt: 'currency' | 'ratio' | 'text';
}

export interface ReportHeaderInfo {
  title: string;          // 合計残高試算表 / 科目別対比表 / 補助科目内訳表
  periodLabel: string;    // 自 2026年 1月 1日 至 2026年 1月31日
  companyName: string;    // クマガイコーポレーション株式会社
  printedAt: string;      // 印刷日時 2026年05月29日
  taxNote: string;        // 【税抜】
  unitNote: string;       // (単位：円)
  sectionNote?: string;   // 【部門】本部 など (補助科目内訳表)
  adjustNote?: string;    // 【整理仕訳の集計条件】...
}

export interface FinancialReport {
  kind: ReportKind;
  header: ReportHeaderInfo;
  columns: ReportColumn[];
  rows: ReportRow[];
  /** 印刷向き(対比表のみ横) */
  landscape?: boolean;
  /** 科目別対比表のヘッダ2段グルーピング(発生/残高) */
  columnGroups?: { label: string; span: number }[];
  /**
   * 非表示ラベル集合を渡して帳票を再構築する(取得済み freee データを再利用・再fetch不要)。
   * 非表示行は計算(小計/合計/構成比)から除外される。生成時に各 fetch が closure を付与。
   * meta.allLabels で全行ラベル(非表示UI用)、isAutoInserted で新規勘定科目を識別。
   */
  rebuild?: (hiddenLabels: Set<string>) => FinancialReport;
  meta: {
    freee_display_type: 'group' | 'account_item';
    start_date: string;
    end_date: string;
    prev_start_date?: string;
    prev_end_date?: string;
    up_to_date: boolean;
    /** freee API が空/未取得だった場合の明示(0埋め禁止) */
    empty?: boolean;
    note?: string;
    /** ロバスト化: テンプレ未登録 → 群判定して自動挿入した科目 */
    autoInserted?: AutoInsertedAccount[];
    /** ロバスト化: 群判定不能でその他(未分類)へ入れた科目(明示警告) */
    unmapped?: UnmappedAccount[];
    /** Excel テンプレにあるが freee に値が無く0表示にした科目(科目廃止対応) */
    zeroFilledRetired?: string[];
  };
}

/** 期間選択 (会計年度=期番号 + 月。fiscal_year/month は使わず内部で start_date/end_date に変換) */
export interface PeriodSelection {
  /** 会計期番号 (80=2025-10〜2026-09) */
  fiscalTerm: number;
  /** 対象会計月 (1=期首=10月 ... 12=期末=9月)。単月時に使用 */
  fiscalMonth: number;
  /** 範囲モード時の開始/終了会計月 */
  rangeStartMonth?: number;
  rangeEndMonth?: number;
  mode: 'single' | 'range';
}

/** 期番号 → 会計期間の暦日 (10月始まり/9月締め)。期首暦年 = 1945 + 期番号 (80期→2025-10開始) */
export function termStartYear(fiscalTerm: number): number {
  return 1945 + fiscalTerm; // 80期 → 2025
}

/** 会計月(1..12, 1=10月) → 暦の {year, month} */
export function fiscalMonthToCalendar(fiscalTerm: number, fiscalMonth: number): { year: number; month: number } {
  const startYear = termStartYear(fiscalTerm); // 期首(10月)の暦年
  // fiscalMonth 1→10月(startYear), 2→11月, 3→12月, 4→1月(startYear+1), ... 12→9月(startYear+1)
  const calMonth0 = (10 - 1) + (fiscalMonth - 1); // 0-based from Jan
  const year = startYear + Math.floor(calMonth0 / 12);
  const month = (calMonth0 % 12) + 1;
  return { year, month };
}

/** 月の末日(yyyy-mm-dd) */
export function lastDayOfMonth(year: number, month: number): string {
  const d = new Date(Date.UTC(year, month, 0));
  return `${year}-${String(month).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
}
